import { Component, Vue } from "vue-property-decorator";
import { CreateElement } from "vue";
import ConsentForm from "@/components/ConsentPage";
import QuestionnaireParent from "@/views/auth/Questionnaire_Parent.tsx";
import { parentConsentCollection } from "@/firebase";
import ModalContainer from "@/components/Modal.vue";

enum PARENT_STATE {
  CONSENT,
  PARENT_INFORMATION,
  QUESTIONNAIRE
}
@Component
export default class Parent extends Vue {
  content: any = [
    [
      "Who we are",
      `This project is led by Dr. Eunice Eunhee Jang. Dr. Jang is Professor in the Department of Applied Psychology and Human Development in Ontario Institute for Studies in Education, University of Toronto. Dr. Jang’s research focuses on improving assessment practice and student learning by examining ways to integrate assessment into teaching and learning. Currently, she and her team are leading this federally funded research project to develop a dynamic assessment system that provides interactive digital interfaces for students, parents, and teachers to support students’ cognitive, metacognitive, and affective growth. Her book, Focus on Assessment (2014), provides practical guidelines for K-12 teachers based on her research on young learners. To learn more, please visit https://www.oise.utoronto.ca/ejanglab/lab-members/eunice/.`
    ],
    [
      "Why BalanceAI",
      `There is a critical need for assessing and supporting students’ literacy skill development along with their global competencies, such as, communication, self-regulation, and collaboration. Traditional paper-and pencil-based tests are not readily able to assess students’ ability to take up and synthesize new information, their abilities to “think about their own thinking” (metacognition), their engagement with the subject matter, their ability to communicate competently in an online interface, and their capacity for self-regulation. We are developing BALANCE in response to these needs. BALANCE can assess the literacy skills that are foundational in schools today and provide scaffolded intervention in areas of metacognition and self-regulated learning skills, which can support students’ literacy development. BALANCE is being designed to build students’ motivation for literacy through engaging literacy tasks that are appropriate for their age and skill level. For more information, please visit https://www.oise.utoronto.ca/ejanglab/research/balance/ for more information.`
    ],
    [
      "About the study",
      `We invite your child to participate in our research project. Students will be asked to complete three assessments of literacy and global competencies. These can be completed in a group setting in your child’s classroom or individually with a member of the research team. There will be no identifiable data collected, such as student names. Upon your approval, we will also seek permission from your child to participate in the project.
      <br/><br/>
      The first is the Balanced Literacy Learning Inventory, which will gather information about students’ global competencies, including attitude, motivation, self-assessment of literacy abilities and emotional regulation while reading and writing. It also collects demographic data. These data will provide critical information to develop learner modeling algorithms for scaffolding, predicting, and tracking students’ growth in BALANCE.
      <br/><br/>
      The second assessment is the Balanced Literacy Assessment, which includes reading passages, multiple choice, short-answer, long-answer questions, and short writing tasks. This assessment is similar to classroom activities and assessments students are already doing. These will take approximately 3-4 hours total (across several sittings) to complete. 
      <br/><br/>
      The third assessment will require students to sit with a member of the research team and individually complete a short assessment called Talk2Me, Jr. This 30-minute activity will involve reading aloud short texts, describing images, and solving puzzles. Students’ speech will be analyzed by an artificial-intelligence-assisted speech diagnostic platform. We will use a small camera to track students’ eye movements and facial expressions as they complete Talk2Me, Jr., but students are not likely to notice the camera because it is attached to the computer. The purpose of collecting this data is to understand which parts of BALANCE may be exciting, confusing, or boring for students.
      <br/><br/>
      We are also requesting that parents and teachers provide information about students’ language learner (ESL or ELL) status, language-related learning exceptionality (such as dyslexia), teaching interventions, and students’ accommodations. Parents can fill out a form about these items either online or in paper format. The primary reason we need this information is because the research team is developing Talk2Me, Jr. and BALANCE to be sensitive to students’ diverse needs, and to meet this goal we need to include students with a broad range of learning differences.
      <br/><br/>
      There is a possibility that children may become upset when thinking about their literacy skills, their beliefs about own abilities, and their emotions when learning. If at any time your child becomes distressed or upset, they have the option of stopping participation in the project. We are providing participating teachers with a list of mental health resources both in and outside of the school. In the unlikely instance that your child becomes distressed, we will connect with your child’s teacher to make the appropriate referral. 
      `
    ],
    [
      "Data Usage",
      `These data will be used for research purposes and to support professional learning. In addition, these data may be included in conference presentations or academic publications to share the achievements of the project with other teachers or researchers. We will not store any personal information or names in our database so that your child remains anonymous. Your child will be assigned an individual research code. We anticipate that your child/guardian will enjoy taking part in this project. If you are willing to allow your child/guardian to participate in this project, we will ensure that all information is kept fully confidential. Students will not be identified in any of the research information. Participation in the project is voluntary—students may withdraw from the project at any point in time, without negative consequences. 
      <br/><br/>
      All data including written records, recordings, and questionnaires will be kept in a locked cabinet and password-protected external hard drive that will only be accessible by the Principal Investigator and graduate research assistants during the conduct of the research and dissemination of results. Students’ data will be stored in in an encrypted cloud-based database, accessible only to the research team and each student’s own teacher. The data will be kept up to five years after the termination of the project. After five years the hard copy data will be shredded and the electronic data on hard drive and encrypted cloud-based database will be reformatted or destroyed to ensure secure disposal.
      <br/><br/>
      If you agree to allow your child to participate in the study, please complete the following consent form and questionnaire.
      
      `
    ],
    [
      "Contact Us",
      `This study has been approved by the University of Toronto Research Ethics Board and Toronto District School Board’s Ethics External Research Review Committee*. For questions about rights of research participants, please contact the Office of Research Ethics at the University of Toronto, by phone at 416-946-3273 or by email at ethics.review@utoronto.ca. And, if you have any questions about the research, we are happy to discuss them with you. We can be reached by phone or email at the numbers below.
<br/><br/>
	
      Dr. Eunice Eunhee Jang						<br/>
      eun.jang@utoronto.ca<br/>
      (416) 978-0238					<br/>	
      OISE/University of Toronto	<br/>			
      252 Bloor Street West, 9th floor<br/>				
      Toronto, Ontario, Canada M5S 1V6	<br/>
      
      `
    ]
  ];

  formModelInformation: any = [
    { key: "firstname", label: "Student's first name:", type: "text" },
    { key: "lastname", label: "Student's last name:", type: "text" },
    { key: "birthday", label: "Student's birthday", type: "text" },
    {
      key: "grade",
      label: "Student's Grade:",
      type: "radio",
      props: [
        // { key: 1, label: "Kindergarden", value: 1 },
        { key: 3, label: "Grade 3", value: 3 },
        { key: 4, label: "Grade 4", value: 4 },
        { key: 5, label: "Grade 5", value: 5 },
        { key: 6, label: "Grade 6", value: 6 },
        { key: 7, label: "Grade 7", value: 7 },
        { key: 8, label: "Grade 8", value: 8 },
        { key: 9, label: "Grade 9", value: 9 },
        { key: 10, label: "Grade 10", value: 10 },
        { key: 11, label: "Grade 11", value: 11 },
        { key: 12, label: "Grade 12", value: 12 }
      ]
    },
    // {
    //   key: "esl_spec",
    //   label: "Is your child in ESL or Special Education?",
    //   type: "radio",
    //   props: [
    //     { key: 1, label: "Yes", value: true },
    //     { key: 2, label: "No", value: false }
    //   ]
    // },
    { key: "schoolAndTeacher", label: "", type: "dropdown" },
    {
      key: "parentName",
      label: "Your name (Name of parent/guardian)",
      type: "text"
    },
    {
      key: "label",
      label:
        "Please check one of the options below to indicate if you want to complete an optional questionnaire.",
      type: "label"
    },
    {
      key: "subLabel1",
      label:
        "We are asking parents to provide information about students’ English learning status (ESL or ELL), learning exceptionalities, as well as any teaching interventions and/or accommodations they receive. Our aim is to develop BalanceAI to be sensitive to students’ diverse needs, as such, we must include students with a broad range of learning differences. If you are willing to provide this information about your child, kindly complete the following questionnaire.",
      type: "label",
      props: { other: "indent" }
    },
    {
      key: "subLabel2",
      label:
        "If you choose to not complete the questionnaire now, we can provide a printed copy for you to complete at your convenience.",
      type: "label",
      props: { other: "indent" }
    },
    {
      key: "consent", // don't change this key
      type: "radio",
      props: [
        {
          key: 1,
          label: "Proceed to online questionnaire",
          value: true
        },
        { key: 2, label: "Receive a paper copy later", value: false }
      ]
    },
    {
      key: "recaptcha",
      type: "checkbox",
      label: "hello",
      disabled: true,
      hidden: true
    }
  ];

  private formModelConsent: any = [
    {
      key: "label",
      label:
        "Please check one of the options below to indicate your consent to the following activities.",
      type: "label"
    },
    {
      key: "subLabel1",
      label: "I have read the above information.",
      type: "label",
      props: { other: "indent" }
    },
    {
      key: "subLabel2",
      label: "I voluntarily agree for my child to participate in the project.",
      type: "label",
      props: { other: "indent" }
    },
    {
      key: "consent", // don't change this key
      type: "radio",
      props: [
        {
          key: 1,
          label: "Agree",
          value: true
        },
        { key: 2, label: "Disagree", value: false }
      ]
    }
  ];

  private proceedToQuestionnaireText =
    "Thank you for giving consent.\n We are also asking parents and teachers to provide information about students’ language status (ESL or ELL), learning exceptionalities, as well as any teaching interventions and/or accommodations they receive. Our aim is to develop BalanceAI to be sensitive to students’ diverse needs, as such, we must include students with a broad range of learning differences. If you are willing to provide this information about your child, kindly complete the following questionnaire.";

  private _state: PARENT_STATE;
  private parentConsentDocID: string | undefined;

  constructor() {
    super();
    this._state = PARENT_STATE.CONSENT;
  }

  created() {
    this._state = PARENT_STATE.CONSENT;
  }

  consentFormData = {};

  render(h: CreateElement) {
    return (
      <span>
        {this._consent()}
        {this._parentInformation()}
        {this._questionnaire()}
      </span>
    );
  }

  private _questionnaire() {
    return (
      this._state === PARENT_STATE.QUESTIONNAIRE && (
        <QuestionnaireParent
          consentDocId={this.parentConsentDocID}
        ></QuestionnaireParent>
      )
    );
  }

  private _consent() {
    return (
      this._state === PARENT_STATE.CONSENT && (
        <ConsentForm
          form-model={this.formModelConsent}
          content={this.content}
          title="Parent Consent Form"
          type="parent"
          collection={parentConsentCollection}
          buttonLabel="Next"
          callbackfn={() => {
            // if consent is True then go on to parent_questionnaire.
            // Only after the questionnaire will the data be submitted
            this._state = PARENT_STATE.PARENT_INFORMATION;
            this.$forceUpdate();
          }}
          consentFormData={this.consentFormData}
        >
          <p slot="description">
            Dear Parents and Guardians,
            <br /> <br />
            The purpose of this letter is to invite your child to participate in
            a research project in which we develop an innovative
            curriculum-based literacy assessment for students in Grades 3-6.
            This assessment, called BALANCE (Blended Assessment of Literacy and
            Numeracy in Computerized Environments), can help improve your
            child’s literacy skills and support him or her to develop the
            ability to self-regulate his or her own learning. The project can
            also provide students, teachers, and parents with high-quality
            information about students’ literacy skills.
          </p>
          <p slot="postDescription">
            If you agree to allow your child to participate in the study, please
            complete the following consent form and questionnaire.
          </p>
        </ConsentForm>
      )
    );
  }

  private _parentInformation() {
    return (
      this._state === PARENT_STATE.PARENT_INFORMATION && (
        <ConsentForm
          form-model={this.formModelInformation}
          content={this.content}
          title="Parent Consent Form"
          type="parent"
          collection={parentConsentCollection}
          buttonLabel="Submit Consent Form"
          callbackfn={() => {
            // if consent is True then go on to parent_questionnaire.
            // Only after the questionnaire will the data be submitted
            this.submitParentInformation();
            this._state = PARENT_STATE.QUESTIONNAIRE;
            this.$forceUpdate();
          }}
          callbackOnConsentReject={() => {
            this.submitParentInformation();
            this.$router.push("/");
          }}
          consentFormData={this.consentFormData}
          showRecaptcha
        >
          <p slot="description">
            Dear Parents and Guardians,
            <br /> <br />
            The purpose of this letter is to invite your child to participate in
            a research project in which we develop an innovative
            curriculum-based literacy assessment for students in Grades 3-6.
            This assessment, called BALANCE (Blended Assessment of Literacy and
            Numeracy in Computerized Environments), can help improve your
            child’s literacy skills and support him or her to develop the
            ability to self-regulate his or her own learning. The project can
            also provide students, teachers, and parents with high-quality
            information about students’ literacy skills.
          </p>
          <p slot="postDescription">
            If you agree to allow your child to participate in the study, please
            complete the following consent form and questionnaire.
          </p>
        </ConsentForm>
      )
    );
  }

  private submitParentInformation() {
    parentConsentCollection
      .add({
        ...this.consentFormData,
        ...{ dateSubmitted: Date.now() }
      })
      .then(docRef => {
        this.parentConsentDocID = docRef.id;
        this.$store.dispatch("addNotification", {
          type: "success",
          value: `Parent Consent Form submitted sucessfully. Thank you for participating.`,
          timeout: 4000,
          icon: "mdi-file-document"
        });
      })
      .catch(err => {
        this.$store.dispatch("addNotification", {
          type: "error",
          value: `Parent Consent Form failed to submit. Please try again.`,
          timeout: 6000,
          icon: "mdi-file-document"
        });
      });
  }
}

const component = Parent;
